import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProjectRecordsTable from "../components/ProjectRecordsTable";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { getProjects } from "../lib/api";

export default function SelectedRecordsPage() {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const companyName = searchParams.get("companyName") || "Selected CSR company";
  const cin = searchParams.get("cin") || "Not specified";
  const pageSize = 100;

  const requestParams = useMemo(
    () => ({
      company_id: companyId,
      page,
      page_size: pageSize,
      ...(searchParams.get("search") ? { search: searchParams.get("search") } : {}),
      ...(searchParams.get("state") ? { state: searchParams.get("state") } : {}),
      ...(searchParams.get("district") ? { district: searchParams.get("district") } : {}),
      ...(searchParams.get("sector") ? { sector: searchParams.get("sector") } : {}),
      ...(searchParams.get("year") ? { year: searchParams.get("year") } : {}),
      ...(searchParams.get("implementation_mode") ? { implementation_mode: searchParams.get("implementation_mode") } : {}),
    }),
    [companyId, page, searchParams],
  );

  const activeCriteria = useMemo(() => {
    const entries = [
      ["Search", searchParams.get("search")],
      ["State", searchParams.get("state")],
      ["District", searchParams.get("district")],
      ["Sector", searchParams.get("sector")],
      ["Financial year", searchParams.get("year")],
      ["Implementation mode", searchParams.get("implementation_mode")],
    ];
    return entries.filter(([, value]) => value).map(([label, value]) => `${label}: ${value}`);
  }, [searchParams]);

  useEffect(() => {
    const loadSelectedRecords = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getProjects(requestParams);
        setRecords(response.items);
        setTotal(response.total);
      } catch (loadError) {
        setError("We couldn't load the selected matching records right now.");
      } finally {
        setLoading(false);
      }
    };

    loadSelectedRecords();
  }, [requestParams]);

  return (
    <div className="site-shell min-h-screen">
      <BrandHeader compact />

      <main className="mx-auto flex w-full max-w-[1700px] flex-col gap-8 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button type="button" variant="outline" className="rounded-none" onClick={() => navigate(-1)} data-testid="selected-records-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Link to={`/company/${companyId}`} data-testid="selected-records-full-profile-link">
            <Button type="button" className="rounded-none bg-slate-950 text-white hover:bg-slate-800">
              View full profile
            </Button>
          </Link>
        </div>

        <section className="section-frame grid gap-6 p-6 md:grid-cols-[1.4fr_0.8fr] md:p-10" data-testid="selected-records-hero">
          <div className="space-y-5">
            <div className="hero-line" />
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Selected matching records</p>
            <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl" data-testid="selected-records-company-name">
              {companyName}
            </h1>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid="selected-records-cin">
              CIN: {cin}
            </p>
            <p className="max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
              This page shows only the project rows that match the search and filter criteria you selected for this company.
            </p>
          </div>

          <Card className="rounded-none border-slate-200 bg-white">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center gap-3 text-slate-950">
                <FileSpreadsheet className="h-5 w-5 text-[#F26522]" />
                <p className="text-lg font-bold">Applied criteria</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="selected-records-active-criteria">
                {activeCriteria.length > 0 ? activeCriteria.map((item) => (
                  <span key={item} className="border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    {item}
                  </span>
                )) : (
                  <span className="text-sm text-slate-500">No extra filters applied.</span>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {error ? (
          <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="selected-records-error-message">
            {error}
          </div>
        ) : null}

        <section className="section-frame p-6 md:p-8" data-testid="selected-records-table-section">
          <ProjectRecordsTable
            records={records}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            emptyMessage="No records match the selected criteria for this company."
            hideSectorColumn={Boolean(searchParams.get("sector"))}
          />
        </section>
      </main>
    </div>
  );
}